import React, { forwardRef, type Ref, useEffect, useState } from 'react';

import { AccountFormControls, Box, EmailInput } from '@opendoor/bricks-next';
import type { TextInput as RNTextInput } from 'react-native';

import { Form } from './shared';

export interface IAccountEmailFormProps {
  /**
   * Name of the account form in analytics events, should be unique within the app
   * Convention: {appName}-{accountFormName}
   */
  analyticsName: string;
  email?: string;
  loading?: boolean;
  onSubmit: (email: string, fullName?: string) => void;
  openForgotPasswordInNewTab?: boolean;
  showForgotPassword?: boolean;
}

const AccountEmailForm = forwardRef(
  (
    {
      showForgotPassword = true,
      analyticsName,
      loading,
      openForgotPasswordInNewTab,
      onSubmit,
      email: initialEmail = '',
    }: IAccountEmailFormProps,
    ref: Ref<RNTextInput>,
  ) => {
    const [prefillEmail, setPrefillEmail] = useState<string>(initialEmail);
    const [email, setEmail] = useState<string>(initialEmail);

    const [hasError, setHasError] = useState<boolean>(false);

    const maybePrefillEmail = () => {
      const url = new URL(window.location.href);
      const params = new URLSearchParams(url.searchParams);

      // Update state on mount only if there is a query param override
      if (params.get('email')) {
        setPrefillEmail(params.get('email') || initialEmail);
      }
    };

    useEffect(() => {
      maybePrefillEmail();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!hasError) {
        onSubmit(email);
      }
      if (ref && 'current' in ref && ref.current) {
        ref.current.focus();
      }
    };

    const handleValidityChange = (validity: string) => {
      setHasError(validity !== 'valid');
      if (validity !== 'valid' && ref && 'current' in ref && ref.current) {
        ref.current.focus();
      }
    };

    // Disable the form if it has an error or if email is not set.
    const isDisabled = hasError || !email;

    return (
      <Form onSubmitCapture={handleSubmit}>
        <Box gap="$4x">
          <EmailInput
            ref={ref}
            analyticsName={analyticsName}
            title="Email"
            getValue={(value) =>
              setEmail(value.unformattedValue == null ? '' : value.unformattedValue)
            }
            onValidationChange={handleValidityChange}
            prefilledValue={prefillEmail}
            fieldRequiredError="This field is required"
            invalidEmailError="Please enter a valid email address"
            size="medium"
          />
          <AccountFormControls
            actionText="Continue with email"
            analyticsName={analyticsName}
            loading={loading}
            disabled={isDisabled}
            openForgotPasswordInNewTab={openForgotPasswordInNewTab}
            submitA11yProps={{ 'aria-label': '' }}
            showForgotPassword={showForgotPassword}
            forgotPasswordText="Forgot your password?"
            forgotPasswordAriaNewWindow="Opens a new window with the forgot password page"
            forgotPasswordAriaSameWindow="Opens the forgot password page"
          />
        </Box>
      </Form>
    );
  },
);

AccountEmailForm.displayName = 'AccountEmailForm';
export default AccountEmailForm;
